import { useState, useCallback } from 'react'
import { ApolloError, useApolloClient } from '@apollo/client'
import { useDeepEffect } from '@retailer-platform/shared-common'
import { queryRetailerLocations, type RetailerLocation } from './utils/queryRetailerLocations'

interface HookResult {
  data: RetailerLocation[]
  loading: boolean
  error: Error
}

/**
 * Queries for multiple calls to fetch retailer locations for the given
 * retailer ids. This will combine the results into a single response.
 *
 * @param ids the ids of the retailers
 */
export const useRetailerLocations = (
  ids: string[],
  includeInactiveLocations?: boolean
): HookResult => {
  const client = useApolloClient()
  const [results, setResults] = useState<HookResult>({
    data: null,
    loading: false,
    error: null,
  })

  const query = useCallback(
    async (ids: string[], includeInactiveLocations?: boolean) => {
      /**
       * Note that we use promises instead of hooks here. this is because
       * we don't know beforehand how many calls we need to do, and
       * that would violate rules of hooks (e.g. all runs should contain
       * the same hooks in the same order)
       */
      const queries = ids.map(id => queryRetailerLocations(client, id, includeInactiveLocations))

      setResults({ data: null, error: null, loading: true })

      const batchedResult = await Promise.all(queries)

      const accumulatedResults = batchedResult.reduce(
        (acc, result) => {
          if (result.errors?.length) {
            acc.errors = acc.errors.concat(result.errors)
          }

          if (result.data) {
            acc.data = acc.data.concat(result.data ?? [])
          }

          return acc
        },
        { errors: [], data: [] }
      )

      if (accumulatedResults.errors.length) {
        accumulatedResults.data = null
      } else {
        accumulatedResults.errors = null
      }

      const error = accumulatedResults.errors
        ? new ApolloError({
            graphQLErrors: accumulatedResults.errors,
            errorMessage: 'Unable to fetch retailer locations',
          })
        : null
      return setResults({ data: accumulatedResults.data, error, loading: false })
    },
    [client]
  )

  useDeepEffect(() => {
    query(ids, includeInactiveLocations)
  }, [ids, includeInactiveLocations, query])

  return results
}
